import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Typography,
    Paper,
    Chip,
    Drawer,
    Box,
    Autocomplete,
    Switch,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ProductData, ProductType, ProductCategory } from '../ProductTypes';
import { toast } from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';
import { styled } from '@mui/material/styles';
import { db, storage, firebase } from '../../../../../Config/firebase';
import { Timestamp } from 'firebase/firestore';

interface ProductFormModalProps {
    serviceProviderId: string;
    open: boolean;
    onClose: () => void;
    onSubmit: (data: Partial<ProductData>, productImages: File[]) => Promise<void>;
    initialData?: ProductData | null;
    isLoading: boolean;
}

interface ProductVariant {
    id: string;
    variantType: string;     // e.g., 'color', 'size', 'material', 'style', etc.
    variantValue: string;
    price: number;
    inventory: number;
    remainingInventory: number;
    sku: string;
}

interface VariantForm {
    variantType: string;
    variantValue: string;
    price: number;
    inventory: number;
}

interface VariantOption {
    type: string;    // e.g., 'size', 'color'
    values: string[]; // e.g., ['S', 'M', 'L'] or ['Red', 'Blue']
}

interface VariantCombination {
    id: string;
    combination: Record<string, string>; // e.g., { size: 'L', color: 'Red' }
    price: number;
    inventory: number;
    remainingInventory: number;
    sku: string;
}

const VARIANT_TYPES = [
    'color',
    'size',
    'material',
    'style',
    'edition',
    'pattern',
    'weight',
    'capacity'
];

// Styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: theme.spacing(1),
    border: '1px solid',
    borderColor: theme.palette.divider,
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        borderColor: theme.palette.primary.main,
        boxShadow: theme.shadows[2]
    }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2)
}));

const FormSection = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(4),
    '& .MuiTextField-root': {
        marginBottom: theme.spacing(2)
    }
}));

const ProductFormModal: React.FC<ProductFormModalProps> = ({
    serviceProviderId,
    open,
    onClose,
    onSubmit,
    initialData,
    isLoading
}) => {
    // Initialize form data with initialData or defaults
    const [formData, setFormData] = useState<Partial<ProductData>>({
        type: ProductType.PRODUCT,
        productName: '',
        description: '',
        basePrice: 0,
        category: ProductCategory.OTHER,
        inventory: 0,
        remainingInventory: 0,
        minOrderQuantity: 1,
        maxOrderQuantity: 100,
        isLive: true,
        tax: 0,
        termsAndConditions: 'Non-refundable',
        weight: 0,
        dimensions: {
            length: 0,
            width: 0,
            height: 0
        },
        productImages: [],
        variants: [],
    });

    // Update image states to handle multiple images
    const [productImageFiles, setProductImageFiles] = useState<File[]>([]);
    const [productImagePreviews, setProductImagePreviews] = useState<string[]>([]);

  
    const [currentVariant, setCurrentVariant] = useState<VariantForm>({
        variantType: '',
        variantValue: '',
        price: 0,
        inventory: 0
    });

    // Add validation state
    const [errors, setErrors] = useState<{
        productName?: string;
        description?: string;
        basePrice?: string;
        variants?: string;
    }>({});

    // Add new state for variant options and combinations
    const [variantOptions, setVariantOptions] = useState<VariantOption[]>([]);
    const [variantCombinations, setVariantCombinations] = useState<VariantCombination[]>([]);
    const [currentVariantType, setCurrentVariantType] = useState('');
    const [currentVariantValues, setCurrentVariantValues] = useState<string[]>([]);

    // Add SEO state
    const [seoData, setSeoData] = useState({
        metaTitle: initialData?.metaTitle || '',
        metaDescription: initialData?.metaDescription || '',
        keywords: initialData?.keywords || [],
    });

    // State for accordion
    const [expandedSection, setExpandedSection] = useState<string | false>(false);

    // Common variant values suggestions (can be expanded)
    const variantSuggestions = {
        color: ['Red', 'Blue', 'Green', 'Black', 'White', 'Yellow', 'Purple', 'Orange', 'Grey'],
        size: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL'],
        material: ['Cotton', 'Polyester', 'Wool', 'Silk', 'Leather', 'Denim', 'Linen'],
        style: ['Casual', 'Formal', 'Sport', 'Classic', 'Modern', 'Vintage'],
    };

    // Update useEffect to properly handle initialData including variants
    useEffect(() => {
        if (open) {
            if (initialData) {
                console.log('Initial Data:', initialData); // Debug log
                setFormData(prev => ({
                    ...prev,
                    ...initialData,
                }));
                
                // Set product images
                if (initialData.productImages?.length > 0) {
                    setProductImagePreviews(initialData.productImages);
                }

                // Handle variants
                if (initialData.variants && initialData.variants.length > 0) {
                    console.log('Loading variants:', initialData.variants); // Debug log
                    
                    // Extract unique variant types and their values
                    const variantMap = new Map<string, Set<string>>();
                    
                    initialData.variants.forEach(variant => {
                        if (variant.combination) {
                            Object.entries(variant.combination).forEach(([type, value]) => {
                                if (!variantMap.has(type)) {
                                    variantMap.set(type, new Set());
                                }
                                variantMap.get(type)?.add(value);
                            });
                        }
                    });

                    // Convert to VariantOption array
                    const options: VariantOption[] = Array.from(variantMap.entries()).map(([type, values]) => ({
                        type,
                        values: Array.from(values)
                    }));

                    console.log('Setting variant options:', options); // Debug log
                    setVariantOptions(options);

                    // Set variant combinations
                    const combinations = initialData.variants.map(variant => ({
                        id: variant.id || uuidv4(),
                        combination: variant.combination,
                        price: variant.price || 0,
                        inventory: variant.inventory || 0,
                        remainingInventory: variant.remainingInventory || 0,
                        sku: variant.sku || generateSKU(initialData.productName, 
                            Object.keys(variant.combination)[0], 
                            Object.values(variant.combination)[0])
                    }));

                    console.log('Setting variant combinations:', combinations); // Debug log
                    setVariantCombinations(combinations);
                }

                // Set SEO data
                setSeoData({
                    metaTitle: initialData.metaTitle || '',
                    metaDescription: initialData.metaDescription || '',
                    keywords: initialData.keywords || [],
                });
            } else {
                // Reset form for new product
                setFormData({
                    type: ProductType.PRODUCT,
                    productName: '',
                    description: '',
                    basePrice: 0,
                    category: ProductCategory.OTHER,
                    inventory: 0,
                    remainingInventory: 0,
                    minOrderQuantity: 1,
                    maxOrderQuantity: 100,
                    isLive: false,
                    tax: 0,
                    termsAndConditions: 'Non-refundable',
                    weight: 0,
                    dimensions: {
                        length: 0,
                        width: 0,
                        height: 0
                    },
                    productImages: [],
                    variants: [],
                    sessionsPurchased: 0,
                });
                setProductImagePreviews([]);
                setVariantOptions([]);
                setVariantCombinations([]);
                setSeoData({
                    metaTitle: '',
                    metaDescription: '',
                    keywords: [],
                });
            }
            setProductImageFiles([]);
        }
    }, [open, initialData]);

    // Update image handlers for multiple images
    const handleProductImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            const newFiles = Array.from(files);
            
            // Check file sizes
            const oversizedFiles = newFiles.filter(file => file.size > 5 * 1024 * 1024);
            if (oversizedFiles.length > 0) {
                toast.error('Some images exceed 5MB limit');
                return;
            }

            // Add to existing files
            setProductImageFiles(prev => [...prev, ...newFiles]);
            
            // Create previews for new files
            newFiles.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setProductImagePreviews(prev => [...prev, reader.result as string]);
                    setFormData(prev => ({
                        ...prev,
                        productImages: [...(prev.productImages || []), reader.result as string]
                    }));
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeProductImage = (index: number) => {
        setProductImagePreviews(prev => prev.filter((_, i) => i !== index));
        setProductImageFiles(prev => prev.filter((_, i) => i !== index));
        setFormData(prev => ({
            ...prev,
            productImages: prev.productImages?.filter((_, i) => i !== index) || []
        }));
    };


    // Generate SKU function
    const generateSKU = (productName: string, variantType: string, variantValue: string) => {
        const cleanProductName = productName.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3).toUpperCase();
        const cleanVariantType = variantType.substring(0, 2).toUpperCase();
        const cleanVariantValue = variantValue.replace(/[^a-zA-Z0-9]/g, '').substring(0, 3).toUpperCase();
        const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `${cleanProductName}-${cleanVariantType}${cleanVariantValue}-${randomNum}`;
    };

    // Validate if form can be submitted
    const canSubmit = () => {
        return (
            formData.productName?.trim() && // Has product name
            formData.basePrice && formData.basePrice > 0 && // Has valid base price
            variantCombinations.length > 0 && // Has at least one variant
            !isLoading // Not currently loading
        );
    };

    // Function to validate form
    const validateForm = () => {
        const newErrors: typeof errors = {};
        
        if (!formData.productName?.trim()) {
            newErrors.productName = 'Product name is required';
        }
        
        if (!formData.basePrice || formData.basePrice <= 0) {
            newErrors.basePrice = 'Valid base price is required';
        }

        if (variantCombinations.length === 0) {
            newErrors.variants = 'At least one variant is required';
        }

        return newErrors;
    };

    // Function to upload images to Firebase Storage
    const uploadImages = async (files: File[]): Promise<string[]> => {
        const uploadPromises = files.map(async (file) => {
            const fileRef = firebase.storageRef(storage, `products/${formData.id || 'new'}/${file.name}`);
            await firebase.uploadBytes(fileRef, file);
            return firebase.getDownloadURL(fileRef);
        });

        return Promise.all(uploadPromises);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setErrors(errors);
            toast.error('Please fill all required fields');
            return;
        }

        // Prepare product data with variants
        const productData: Partial<ProductData> = {
            ...formData,
            variants: variantCombinations.map(vc => ({
                id: vc.id,
                combination: vc.combination,
                price: Number(vc.price),
                inventory: Number(vc.inventory),
                remainingInventory: Number(vc.remainingInventory),
                sku: vc.sku
            })),
            metaTitle: seoData.metaTitle,
            metaDescription: seoData.metaDescription,
            keywords: seoData.keywords
        };

        // Call the parent's onSubmit with the new data and image files
        await onSubmit(productData, productImageFiles);
    };

    // Handle adding multiple variant values
    const handleAddVariantOption = () => {
        if (!currentVariantType || currentVariantValues.length === 0) {
            toast.error('Please select a variant type and at least one value');
            return;
        }

        setVariantOptions(prev => {
            const existingOption = prev.find(opt => opt.type === currentVariantType);
            if (existingOption) {
                // Add new values to existing type if not already present
                const newValues = currentVariantValues.filter(
                    value => !existingOption.values.includes(value)
                );
                if (newValues.length === 0) {
                    toast.error('These variant values already exist');
                    return prev;
                }
                const updatedOptions = prev.map(opt =>
                    opt.type === currentVariantType
                        ? { ...opt, values: [...opt.values, ...newValues] }
                        : opt
                );
                setTimeout(() => generateCombinations(updatedOptions), 0);
                return updatedOptions;
            }

            // Add new variant type with values
            const newOptions = [...prev, { type: currentVariantType, values: currentVariantValues }];
            setTimeout(() => generateCombinations(newOptions), 0);
            return newOptions;
        });

        // Reset inputs
        setCurrentVariantType('');
        setCurrentVariantValues([]);
    };

    // Handle removing a variant option or value
    const handleRemoveVariantOption = (optionType: string, valueToRemove?: string) => {
        setVariantOptions(prev => {
            let updatedOptions;
            if (valueToRemove) {
                // Remove specific value from variant type
                updatedOptions = prev.map(opt => {
                    if (opt.type === optionType) {
                        return {
                            ...opt,
                            values: opt.values.filter(v => v !== valueToRemove)
                        };
                    }
                    return opt;
                });
                // Remove variant type if no values left
                updatedOptions = updatedOptions.filter(opt => opt.values.length > 0);
            } else {
                // Remove entire variant type
                updatedOptions = prev.filter(opt => opt.type !== optionType);
            }
            
            // Generate new combinations after removing variant
            setTimeout(() => generateCombinations(updatedOptions), 0);
            return updatedOptions;
        });
    };

    // Update generateCombinations to handle empty options
    const generateCombinations = (options: VariantOption[]) => {
        if (options.length === 0) {
            setVariantCombinations([]);
            return;
        }

        const generateHelper = (
            current: Record<string, string>, 
            optionIndex: number
        ): Record<string, string>[] => {
            if (optionIndex === options.length) {
                return [current];
            }

            const currentOption = options[optionIndex];
            const combinations: Record<string, string>[] = [];

            currentOption.values.forEach(value => {
                const newCurrent = { ...current, [currentOption.type]: value };
                combinations.push(...generateHelper(newCurrent, optionIndex + 1));
            });

            return combinations;
        };

        const allCombinations = generateHelper({}, 0);
        
        setVariantCombinations(prev => {
            const newCombinations = allCombinations.map(combination => {
                // Try to find existing combination with same values
                const existing = prev.find(vc => 
                    Object.entries(combination).every(
                        ([key, value]) => vc.combination[key] === value
                    ) &&
                    Object.keys(vc.combination).length === Object.keys(combination).length
                );

                if (existing) {
                    return existing;
                }

                return {
                    id: uuidv4(),
                    combination,
                    price: formData.basePrice || 0,
                    inventory: 0,
                    remainingInventory: 0,
                    sku: generateSKU(
                        formData.productName,
                        Object.values(combination).join('-'),
                        ''
                    )
                };
            });

            return newCombinations;
        });
    };

    // Handle live status toggle
    const handleLiveStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({
            ...prev,
            isLive: event.target.checked
        }));
    };

    const handleAccordionChange = (panel: string) => (
        _event: React.SyntheticEvent,
        isExpanded: boolean
    ) => {
        setExpandedSection(isExpanded ? panel : false);
    };

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiDrawer-paper': {
                    width: {
                        xs: '100%',
                        sm: '100%',
                        md: '600px' // Reduced from 800px
                    },
                }
            }}
        >
            <div className="h-full flex flex-col">
                {/* Header with Live Status */}
                <Box sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {initialData ? 'Edit Product' : 'Add New Product'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isLive}
                                    onChange={handleLiveStatusChange}
                                    color="success"
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: formData.isLive ? 'success.main' : 'text.secondary' }}>
                                    {formData.isLive ? 'Live' : 'Draft'}
                                </Typography>
                            }
                        />
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                {/* Styled Content */}
                <Box className="flex-1 overflow-y-auto">
                    <Box sx={{ p: 3 }}> {/* Reduced padding */}
                        <form onSubmit={handleSubmit} className="space-y-4"> {/* Reduced spacing */}
                            {/* Basic Information */}
                            <StyledPaper sx={{ p: 2 }}> {/* Reduced padding */}
                                <FormSection sx={{ mb: 2 }}> {/* Reduced margin */}
                                    <SectionTitle variant="subtitle2">Basic Information</SectionTitle>
                                    <Box sx={{ display: 'grid', gap: 2 }}>
                                        <TextField
                                            label="Product Name*"
                                            value={formData.productName}
                                            onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                                            error={!!errors.productName}
                                            helperText={errors.productName}
                                            size="small"
                                            fullWidth
                                            required
                                        />
                                        <TextField
                                            label="Description*"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            error={!!errors.description}
                                            helperText={errors.description}
                                            multiline
                                            rows={2}
                                            size="small"
                                            fullWidth
                                            required
                                        />
                                        
                                        {/* Category and Type Selection */}
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                            <FormControl size="small" required>
                                                <InputLabel>Category</InputLabel>
                                                <Select
                                                    value={formData.category}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        category: e.target.value as ProductCategory
                                                    })}
                                                    label="Category"
                                                >
                                                    {Object.values(ProductCategory).map((category) => (
                                                        <MenuItem key={category} value={category}>
                                                            {category.replace(/([A-Z])/g, ' $1').trim()}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl size="small" required>
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={formData.type}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        type: e.target.value as ProductType
                                                    })}
                                                    label="Type"
                                                >
                                                    {Object.values(ProductType).map((type) => (
                                                        <MenuItem key={type} value={type}>
                                                            {type.replace(/([A-Z])/g, ' $1').trim()}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>
                                </FormSection>
                            </StyledPaper>

                            {/* Media Section - Make it more compact */}
                            <StyledPaper sx={{ p: 2 }}>
                                <FormSection>
                                    <SectionTitle>Media</SectionTitle>
                                    <div className="space-y-4">
                                        
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {/* Image Upload Button */}
                                            <label className="cursor-pointer">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    multiple
                                                    onChange={handleProductImageChange}
                                                    className="hidden"
                                                />
                                                <Paper 
                                                    className="flex flex-col items-center justify-center p-4 border-2 border-dashed"
                                                    sx={{ borderColor: 'primary.main', height: '150px' }}
                                                >
                                                    <AddPhotoAlternateIcon color="primary" sx={{ fontSize: 40 }} />
                                                    <Typography variant="caption" color="textSecondary">
                                                        Add Images
                                                    </Typography>
                                                </Paper>
                                            </label>

                                            {/* Image Previews */}
                                            {productImagePreviews.map((preview, index) => (
                                                <Paper 
                                                    key={index} 
                                                    className="relative"
                                                    sx={{ height: '150px' }}
                                                >
                                                    <img
                                                        src={preview}
                                                        alt={`Product ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => removeProductImage(index)}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            right: 4,
                                                            bgcolor: 'background.paper'
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Paper>
                                            ))}
                                        </div>
                                    </div>
                                </FormSection>
                            </StyledPaper>

                            {/* Updated Variants Section */}
                            <StyledPaper>
                                <FormSection>
                                    <SectionTitle>
                                        Product Variants
                                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                                            ({variantCombinations.length} combinations)
                                        </Typography>
                                    </SectionTitle>

                                    {/* Variant Input Section */}
                                    <Box sx={{ 
                                        display: 'grid', 
                                        gridTemplateColumns: { xs: '1fr', md: '1fr 2fr auto' },
                                        gap: 2,
                                        alignItems: 'start',
                                        mb: 3
                                    }}>
                                        <FormControl size="small">
                                            <InputLabel>Variant Type</InputLabel>
                                            <Select
                                                value={currentVariantType}
                                                onChange={(e) => setCurrentVariantType(e.target.value)}
                                                label="Variant Type"
                                            >
                                                {VARIANT_TYPES.map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {type.charAt(0).toUpperCase() + type.slice(1)}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>

                                        <Autocomplete
                                            multiple
                                            size="small"
                                            value={currentVariantValues}
                                            onChange={(_, newValues) => setCurrentVariantValues(newValues)}
                                            options={currentVariantType ? (variantSuggestions[currentVariantType as keyof typeof variantSuggestions] || []) : []}
                                            freeSolo
                                            renderTags={(value: string[], getTagProps) =>
                                                value.map((option: string, index: number) => (
                                                    <Chip
                                                        variant="outlined"
                                                        label={option}
                                                        size="small"
                                                        {...getTagProps({ index })}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Variant Values"
                                                    placeholder={currentVariantType ? `Add ${currentVariantType} values` : "Select type first"}
                                                />
                                            )}
                                        />

                                        <Button
                                            variant="contained"
                                            onClick={handleAddVariantOption}
                                            startIcon={<AddIcon />}
                                            disabled={!currentVariantType || currentVariantValues.length === 0}
                                            sx={{ height: '40px' }}
                                        >
                                            Add
                                        </Button>
                                    </Box>

                                    {/* Variant Options Display */}
                                    <Box sx={{ mb: 3 }}>
                                        {variantOptions.map((option) => (
                                            <StyledPaper 
                                                key={option.type}
                                                sx={{ 
                                                    mb: 2,
                                                    bgcolor: 'background.default',
                                                    position: 'relative'
                                                }}
                                            >
                                                <Box sx={{ 
                                                    display: 'flex', 
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    mb: 1
                                                }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {option.type.charAt(0).toUpperCase() + option.type.slice(1)}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemoveVariantOption(option.type)}
                                                        sx={{ color: 'error.main' }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {option.values.map((value) => (
                                                        <Chip
                                                            key={value}
                                                            label={value}
                                                            size="small"
                                                            onDelete={() => handleRemoveVariantOption(option.type, value)}
                                                            sx={{ 
                                                                bgcolor: 'background.paper',
                                                                '&:hover': {
                                                                    bgcolor: 'action.hover'
                                                                }
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </StyledPaper>
                                        ))}
                                    </Box>

                                    {/* Variant Combinations Display */}
                                    {variantCombinations.length > 0 && (
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                                Variant Combinations
                                            </Typography>
                                            <Box sx={{ 
                                                display: 'grid', 
                                                gap: 2,
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                pr: 1
                                            }}>
                                                {variantCombinations.map((combination) => (
                                                    <StyledPaper 
                                                        key={combination.id}
                                                        sx={{ 
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' },
                                                            gap: 2,
                                                            alignItems: 'center',
                                                            bgcolor: 'background.default'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                            {Object.entries(combination.combination).map(([type, value]) => (
                                                                <Chip
                                                                    key={`${type}-${value}`}
                                                                    label={`${type}: ${value}`}
                                                                    size="small"
                                                                    sx={{ bgcolor: 'background.paper' }}
                                                                />
                                                            ))}
                                                        </Box>
                                                        <TextField
                                                            label="Price"
                                                            type="number"
                                                            size="small"
                                                            value={combination.price}
                                                            onChange={(e) => {
                                                                setVariantCombinations(prev =>
                                                                    prev.map(vc =>
                                                                        vc.id === combination.id
                                                                            ? { ...vc, price: Number(e.target.value) }
                                                                            : vc
                                                                    )
                                                                );
                                                            }}
                                                            InputProps={{
                                                                startAdornment: <span className="text-gray-500 mr-1">₹</span>,
                                                            }}
                                                        />
                                                        <TextField
                                                            label="Inventory"
                                                            type="number"
                                                            size="small"
                                                            value={combination.inventory}
                                                            onChange={(e) => {
                                                                setVariantCombinations(prev =>
                                                                    prev.map(vc =>
                                                                        vc.id === combination.id
                                                                            ? { 
                                                                                ...vc, 
                                                                                inventory: Number(e.target.value),
                                                                                remainingInventory: Number(e.target.value)
                                                                            }
                                                                            : vc
                                                                        )
                                                                    );
                                                                }}
                                                        />
                                                    </StyledPaper>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </FormSection>
                            </StyledPaper>

                            {/* Pricing Section - More compact */}
                            <StyledPaper sx={{ p: 2 }}>
                                <FormSection sx={{ mb: 2 }}>
                                    <SectionTitle variant="subtitle2">Pricing</SectionTitle>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                        <TextField
                                            label="Base Price*"
                                            type="number"
                                            value={formData.basePrice || ''}
                                            onChange={(e) => setFormData({ ...formData, basePrice: parseFloat(e.target.value) || 0 })}
                                            size="small"
                                            InputProps={{
                                                startAdornment: <span className="text-gray-500 mr-1">₹</span>,
                                            }}
                                        />
                                        <TextField
                                            label="Tax (%)"
                                            type="number"
                                            value={formData.tax || ''}
                                            onChange={(e) => setFormData({ ...formData, tax: parseFloat(e.target.value) || 0 })}
                                            size="small"
                                        />
                                    </Box>
                                </FormSection>
                            </StyledPaper>

                            {/* Advanced Details Accordion */}
                            <StyledPaper sx={{ p: 0 }}>
                                <Accordion
                                    expanded={expandedSection === 'advanced'}
                                    onChange={handleAccordionChange('advanced')}
                                    sx={{
                                        '&.MuiAccordion-root': {
                                            boxShadow: 'none',
                                            '&:before': {
                                                display: 'none',
                                            },
                                        }
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            borderBottom: expandedSection === 'advanced' ? 1 : 0,
                                            borderColor: 'divider',
                                            '& .MuiAccordionSummary-content': {
                                                margin: '12px 0'
                                            }
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            Advanced Details
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ p: 3 }}>
                                        {/* SEO Settings */}
                                        <FormSection sx={{ mb: 4 }}>
                                            <SectionTitle variant="subtitle2">SEO Settings</SectionTitle>
                                            <Box sx={{ display: 'grid', gap: 2 }}>
                                                <TextField
                                                    label="Meta Title"
                                                    value={seoData.metaTitle}
                                                    onChange={(e) => setSeoData({ ...seoData, metaTitle: e.target.value })}
                                                    size="small"
                                                    fullWidth
                                                />
                                                <TextField
                                                    label="Meta Description"
                                                    value={seoData.metaDescription}
                                                    onChange={(e) => setSeoData({ ...seoData, metaDescription: e.target.value })}
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    fullWidth
                                                />
                                                <Autocomplete
                                                    multiple
                                                    size="small"
                                                    value={seoData.keywords}
                                                    onChange={(_, newValue) => setSeoData({ ...seoData, keywords: newValue })}
                                                    options={[]}
                                                    freeSolo
                                                    renderTags={(value: string[], getTagProps) =>
                                                        value.map((option: string, index: number) => (
                                                            <Chip
                                                                variant="outlined"
                                                                label={option}
                                                                size="small"
                                                                {...getTagProps({ index })}
                                                            />
                                                        ))
                                                    }
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Keywords"
                                                            placeholder="Add keywords"
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                            </Box>
                                        </FormSection>

                                        {/* Shipping Details */}
                                        <FormSection>
                                            <SectionTitle variant="subtitle2">Shipping Details</SectionTitle>
                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2 }}>
                                                <TextField
                                                    label="Weight (kg)"
                                                    type="number"
                                                    value={formData.weight || ''}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        weight: parseFloat(e.target.value) || 0
                                                    })}
                                                    size="small"
                                                />
                                                <TextField
                                                    label="Length (cm)"
                                                    type="number"
                                                    value={formData.dimensions?.length || ''}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        dimensions: {
                                                            ...formData.dimensions,
                                                            length: parseFloat(e.target.value) || 0
                                                        }
                                                    })}
                                                    size="small"
                                                />
                                                <TextField
                                                    label="Width (cm)"
                                                    type="number"
                                                    value={formData.dimensions?.width || ''}
                                                    onChange={(e) => setFormData({
                                                        ...formData,
                                                        dimensions: {
                                                            ...formData.dimensions,
                                                            width: parseFloat(e.target.value) || 0
                                                        }
                                                    })}
                                                    size="small"
                                                />
                                            </Box>
                                        </FormSection>
                                    </AccordionDetails>
                                </Accordion>
                            </StyledPaper>
                        </form>
                    </Box>
                </Box>

                {/* Updated Footer */}
                <Box sx={{
                    p: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}>
                    <div className="flex justify-between items-center">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {!canSubmit() && (
                                <Typography variant="caption" color="error">
                                    {!formData.productName?.trim() && "Product name required • "}
                                    {(!formData.basePrice || formData.basePrice <= 0) && "Base price required • "}
                                    {variantCombinations.length === 0 && "At least one variant required"}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                size="small"
                                disabled={isLoading}
                            >
                                Cancel
                            </Button>
                            {initialData?.id && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    onClick={() => deleteProduct(initialData.id)}
                                    size="small"
                                    disabled={isLoading}
                                >
                                    Delete
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={!canSubmit()}
                                size="small"
                                startIcon={isLoading && <CircularProgress size={20} />}
                            >
                                {initialData ? 'Update' : 'Create'} Product
                            </Button>
                        </Box>
                    </div>
                </Box>
            </div>
        </Drawer>
    );
};

export default ProductFormModal;